/**
 * EncounterDatabase - Data definitions for all elite and boss encounters
 * Each encounter has unique piece compositions, AI configs, obstacles, and flavor text
 */

import { PieceType, Square, AIConfig, Obstacle } from '../../types';

export interface EncounterDefinition {
  id: string;
  name: string;
  title: string;
  flavor: string;
  portraitEmoji: string;
  accentColor: number;
  enemyPieces: Array<{ type: PieceType; square: Square }>;
  aiConfig: AIConfig;
  obstacles?: Obstacle[];
  battleTagline?: string;
  chessClock?: boolean; // Force chess clock for this encounter
  fogOfWar?: boolean; // Enable fog of war for this encounter
}

// Act 1 elite pool (floors ~4): 6x6 boards (b-g, ranks 2-7)
// Act 2 elite pool (floors ~8): 7x7 boards (b-h, ranks 2-8)
// Boss pool (floor 12): 8x8 boards (full)

export const ENCOUNTER_DATABASE: Record<string, EncounterDefinition> = {

  // === ACT 1 ELITES (6x6 board) ===

  elite_iron_phalanx: {
    id: 'elite_iron_phalanx',
    name: 'The Iron Phalanx',
    title: 'ELITE ENCOUNTER',
    flavor: 'A wall of shields and spears blocks the corridor ahead. The Iron Phalanx has held this passage for a hundred years, and they do not intend to yield it today.',
    portraitEmoji: '🛡️',
    accentColor: 0x7f8c8d,
    enemyPieces: [
      { type: 'p', square: 'b7' },
      { type: 'p', square: 'c7' },
      { type: 'p', square: 'd7' },
      { type: 'k', square: 'e7' },
      { type: 'n', square: 'f7' },
      { type: 'p', square: 'g7' },
      { type: 'p', square: 'b6' },
      { type: 'p', square: 'g6' },
    ],
    aiConfig: { depth: 4, moveTime: 200, difficulty: 'medium' },
    obstacles: [
      { square: 'd5', type: 'water' },
      { square: 'e5', type: 'water' },
    ],
    battleTagline: 'Break through the wall of shields!',
  },

  elite_shadowbeak: {
    id: 'elite_shadowbeak',
    name: 'Shadowbeak',
    title: 'ELITE ENCOUNTER',
    flavor: 'Twin towers loom in the darkness, connected by corridors of shadow. Shadowbeak perches between them, watching with unblinking eyes. Its rooks slide through the dark like silent sentinels.',
    portraitEmoji: '🦅',
    accentColor: 0x2c3e50,
    enemyPieces: [
      { type: 'r', square: 'b7' },
      { type: 'p', square: 'c7' },
      { type: 'b', square: 'd7' },
      { type: 'k', square: 'e7' },
      { type: 'p', square: 'f7' },
      { type: 'r', square: 'g7' },
    ],
    aiConfig: { depth: 5, moveTime: 300, difficulty: 'hard' },
    obstacles: [
      { square: 'b5', type: 'wall', blocksDirection: 'east' },
      { square: 'g5', type: 'wall', blocksDirection: 'west' },
    ],
    battleTagline: 'Silence the rook corridors!',
  },

  // === ACT 2 ELITES (7x7 board) ===

  elite_tumbling_jester: {
    id: 'elite_tumbling_jester',
    name: 'The Tumbling Jester',
    title: 'ELITE ENCOUNTER',
    flavor: 'Bells jingle in the darkness as four shapes tumble and leap through the air. The Jester laughs — a sound like breaking glass. "Come play!" it cries. "My ponies love to dance!"',
    portraitEmoji: '🃏',
    accentColor: 0xe74c3c,
    enemyPieces: [
      { type: 'n', square: 'c8' },
      { type: 'n', square: 'e8' },
      { type: 'k', square: 'f8' },
      { type: 'n', square: 'g8' },
      { type: 'n', square: 'd7' },
    ],
    aiConfig: { depth: 6, moveTime: 400, difficulty: 'hard' },
    obstacles: [
      { square: 'c5', type: 'water' },
      { square: 'e5', type: 'water' },
      { square: 'g5', type: 'water' },
    ],
    battleTagline: 'Stop the knights from dancing!',
  },

  elite_grand_inquisitor: {
    id: 'elite_grand_inquisitor',
    name: 'The Grand Inquisitor',
    title: 'ELITE ENCOUNTER',
    flavor: 'Robed in white and gold, the Grand Inquisitor surveys the board from behind a fortress of walls. Her queen and bishops move with terrible precision. "Confess," she whispers. "It will be easier."',
    portraitEmoji: '⚜️',
    accentColor: 0xf1c40f,
    enemyPieces: [
      { type: 'b', square: 'c8' },
      { type: 'p', square: 'd8' },
      { type: 'k', square: 'e8' },
      { type: 'p', square: 'f8' },
      { type: 'b', square: 'g8' },
      { type: 'q', square: 'e7' },
    ],
    aiConfig: { depth: 7, moveTime: 500, difficulty: 'expert' },
    obstacles: [
      { square: 'd6', type: 'wall', blocksDirection: 'south' },
      { square: 'f6', type: 'wall', blocksDirection: 'south' },
      { square: 'e6', type: 'wall', blocksDirection: 'west' },
      { square: 'e6', type: 'wall', blocksDirection: 'east' },
    ],
    battleTagline: 'Survive the Inquisition!',
  },

  elite_bog_warden: {
    id: 'elite_bog_warden',
    name: 'The Bog Warden',
    title: 'ELITE ENCOUNTER',
    flavor: 'The passage drowns in black water. The Bog Warden wades through it as though it were air, her bishops gliding across the flooded board while your own pieces flounder at the water\'s edge.',
    portraitEmoji: '🐸',
    accentColor: 0x16a085,
    enemyPieces: [
      { type: 'b', square: 'b7' },
      { type: 'p', square: 'c7' },
      { type: 'k', square: 'd7' },
      { type: 'q', square: 'e7' },
      { type: 'p', square: 'f7' },
      { type: 'b', square: 'g7' },
    ],
    aiConfig: { depth: 5, moveTime: 300, difficulty: 'hard' },
    obstacles: [
      { square: 'c5', type: 'water' },
      { square: 'd5', type: 'water' },
      { square: 'e5', type: 'water' },
      { square: 'f5', type: 'water' },
    ],
    battleTagline: 'Wade through the drowned board!',
  },

  elite_twin_castellans: {
    id: 'elite_twin_castellans',
    name: 'The Twin Castellans',
    title: 'ELITE ENCOUNTER',
    flavor: 'Two brothers in iron command this keep, and neither has lost a siege. Their rooks rake the long files; their walls split the battlefield into killing corridors. To beat them you must beat them both.',
    portraitEmoji: '🏰',
    accentColor: 0x95a5a6,
    enemyPieces: [
      { type: 'r', square: 'b8' },
      { type: 'p', square: 'c8' },
      { type: 'k', square: 'e8' },
      { type: 'q', square: 'f8' },
      { type: 'r', square: 'h8' },
      { type: 'p', square: 'd7' },
      { type: 'p', square: 'f7' },
    ],
    aiConfig: { depth: 6, moveTime: 450, difficulty: 'hard' },
    obstacles: [
      { square: 'd6', type: 'wall', blocksDirection: 'east' },
      { square: 'e6', type: 'wall', blocksDirection: 'west' },
      { square: 'd5', type: 'wall', blocksDirection: 'east' },
      { square: 'e5', type: 'wall', blocksDirection: 'west' },
    ],
    battleTagline: 'Storm the divided keep!',
  },

  // === BOSSES (8x8 board) ===

  boss_clockwork_marshal: {
    id: 'boss_clockwork_marshal',
    name: 'The Clockwork Marshal',
    title: 'BOSS FIGHT',
    flavor: 'Gears grind and pistons hiss as the Clockwork Marshal takes the field. Every piece moves with mechanical precision — rooks where queens should be, queens where rooks belong. In this army, nothing is where you expect it.',
    portraitEmoji: '⚙️',
    accentColor: 0xe67e22,
    enemyPieces: [
      { type: 'q', square: 'a8' },
      { type: 'n', square: 'b8' },
      { type: 'b', square: 'c8' },
      { type: 'r', square: 'd8' },
      { type: 'k', square: 'e8' },
      { type: 'b', square: 'f8' },
      { type: 'n', square: 'g8' },
      { type: 'q', square: 'h8' },
      { type: 'p', square: 'a7' },
      { type: 'p', square: 'b7' },
      { type: 'p', square: 'c7' },
      { type: 'p', square: 'd6' },
      { type: 'p', square: 'e7' },
      { type: 'p', square: 'f6' },
      { type: 'p', square: 'g7' },
      { type: 'p', square: 'h7' },
    ],
    aiConfig: { depth: 8, moveTime: 2000, difficulty: 'expert' },
    obstacles: [
      { square: 'd5', type: 'void' },
      { square: 'e4', type: 'void' },
    ],
    battleTagline: 'Dismantle the machine!',
  },

  boss_mirror_queen: {
    id: 'boss_mirror_queen',
    name: 'The Mirror Queen',
    title: 'BOSS FIGHT',
    flavor: 'She sits on a throne of shattered glass, three reflections of herself standing guard. The Mirror Queen smiles — and three identical smiles answer. "Which of us is real?" she asks. "Does it matter?"',
    portraitEmoji: '👑',
    accentColor: 0x9b59b6,
    enemyPieces: [
      { type: 'p', square: 'a8' },
      { type: 'q', square: 'b8' },
      { type: 'p', square: 'c8' },
      { type: 'q', square: 'd8' },
      { type: 'k', square: 'e8' },
      { type: 'q', square: 'f8' },
      { type: 'p', square: 'g8' },
      { type: 'p', square: 'h8' },
      { type: 'p', square: 'a7' },
      { type: 'p', square: 'h7' },
    ],
    aiConfig: { depth: 8, moveTime: 2000, difficulty: 'expert' },
    obstacles: [
      { square: 'c5', type: 'water' },
      { square: 'f5', type: 'water' },
      { square: 'c4', type: 'water' },
      { square: 'f4', type: 'water' },
    ],
    battleTagline: 'Shatter every reflection!',
  },
  boss_dread_gambit: {
    id: 'boss_dread_gambit',
    name: 'The Dread Gambit',
    title: 'BOSS FIGHT',
    flavor: 'It offers you pieces like a merchant offers fruit — take one, take two, take all you like. Every capture tightens the noose. The Dread Gambit has never needed to win a single exchange to win the game.',
    portraitEmoji: '🎭',
    accentColor: 0x8e44ad,
    enemyPieces: [
      { type: 'r', square: 'a8' },
      { type: 'b', square: 'b8' },
      { type: 'q', square: 'd8' },
      { type: 'k', square: 'e8' },
      { type: 'b', square: 'g8' },
      { type: 'r', square: 'h8' },
      { type: 'n', square: 'c7' },
      { type: 'n', square: 'f7' },
      { type: 'p', square: 'a7' },
      { type: 'p', square: 'b6' },
      { type: 'p', square: 'd7' },
      { type: 'p', square: 'e7' },
      { type: 'p', square: 'g6' },
      { type: 'p', square: 'h7' },
    ],
    aiConfig: { depth: 8, moveTime: 2000, difficulty: 'expert' },
    obstacles: [
      { square: 'c4', type: 'void' },
      { square: 'f4', type: 'void' },
      { square: 'd5', type: 'water' },
      { square: 'e5', type: 'water' },
    ],
    battleTagline: 'Refuse the poisoned offer!',
  },
};

// Encounter pools for selection
export const ACT1_ELITE_IDS = ['elite_iron_phalanx', 'elite_shadowbeak', 'elite_bog_warden'];
export const ACT2_ELITE_IDS = ['elite_tumbling_jester', 'elite_grand_inquisitor', 'elite_twin_castellans'];
export const BOSS_IDS = ['boss_clockwork_marshal', 'boss_mirror_queen', 'boss_dread_gambit'];
